import { Icon, IconName } from './Icon';
import { Typography } from './Typography';

import { FC, ReactNode } from 'react';
import { tv } from 'tailwind-variants';

export type BannerSeverity = 'info' | 'success' | 'warning' | 'error' | 'neutral';

export interface BannerProps {
  severity: BannerSeverity;
  label: React.ReactNode;
  action?: ReactNode;
  href?: string;
  iconName?: IconName;
  className?: string;
}

const bannerTV = tv({
  base: 'flex w-full flex-row items-center gap-x-2 rounded-md p-3',
  variants: {
    severity: {
      info: 'bg-sem-l-surf-info fill-sem-l-icon-info text-sem-l-text-info',
      success: 'bg-sem-l-surf-success fill-sem-l-icon-success text-sem-l-text-success',
      warning: 'bg-sem-l-surf-warning fill-sem-l-icon-warning text-sem-l-text-warning',
      error: 'bg-sem-l-surf-error fill-sem-l-icon-error text-sem-l-text-error',
      neutral: 'bg-sem-l-surf-secondary fill-sem-l-icon-medium text-sem-l-text-high',
    },
  },
});

const iconForSeverity: Record<BannerSeverity, IconName> = {
  info: 'info',
  success: 'check-circle-filled',
  warning: 'warn',
  error: 'warn',
  neutral: 'info',
};

const Banner: FC<BannerProps> = ({ severity, label, action, href, iconName, className }) => {
  const style = bannerTV({ severity, className });
  const effectiveIconName = iconName ?? iconForSeverity[severity];
  const role = severity === 'error' || severity === 'warning' ? 'alert' : 'status';

  const body = (
    <div className="flex w-full flex-row items-center gap-x-1">
      <Icon name={effectiveIconName} size="20" className="fill-inherit" />
      {typeof label === 'string' ? (
        <Typography className="whitespace-pre-line text-inherit" size="rabbit">
          {label}
        </Typography>
      ) : (
        label
      )}
    </div>
  );

  const trailing = href ? <Icon name="chevron-right" size="20" className="fill-inherit" /> : action;

  if (href) {
    return (
      <a href={href} className={style} role={role}>
        {body}
        {trailing}
      </a>
    );
  }
  return (
    <div className={style} role={role}>
      {body}
      {trailing}
    </div>
  );
};

export { Banner };
